import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import type { GqlSuccessResponse } from '@atlassian/conversation-assistant-utils/graphqlUtils';

import { fetchAgg } from '../agg/index.ts';

import {
	ConfluencePagesQuery,
	GetHelpCenterByIdQuery,
	GetSpacesQuery,
	JiraProjectsQuery,
	SearchQuery,
} from './gql-query/index.ts';
import {
	type AggEntitiesResponse,
	type AggEntitiesVariables,
	type ConfluencePagesResponse,
	type ConfluenceSpacesResponse,
	type HelpCenterByIdResponse,
	type HelpCenterByIdVariables,
	type JiraProjectsResponse,
	type KnowledgeFilterSearchResult,
	type ObjectResolveItemResponse,
	type SearchListSpacesResponse,
	type SearchListSpacesVariables,
} from './types.ts';

type EntityType = 'space' | 'page' | 'project';

export interface KnowledgeFilterService {
	filterResults(options: {
		query: string;
		entityType: EntityType;
		confluenceSpaces?: string[];
		projectTypes?: string[];
	}): Promise<KnowledgeFilterSearchResult[]>;
	resolveObjects(options: {
		query: string;
		entityType: EntityType;
	}): Promise<KnowledgeFilterSearchResult[] | null>;
	getEntitiesByAris(options: { aris: string[] }): Promise<KnowledgeFilterSearchResult[]>;
}

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
	return value !== null && value !== undefined;
}

export class KnowledgeFilterServiceImpl implements KnowledgeFilterService {
	constructor(private siteId: string) {}

	private entityTypeToAti = (type: EntityType) => {
		switch (type) {
			case 'space':
				return 'ati:cloud:confluence:space';
			case 'page':
				return 'ati:cloud:confluence:page';
			case 'project':
				return 'ati:cloud:jira:project';
		}
	};

	filterResults = async ({
		query,
		entityType,
		confluenceSpaces = [],
		projectTypes = [],
	}: {
		query: string;
		entityType: EntityType;
		confluenceSpaces?: string[];
		projectTypes?: string[];
	}): Promise<KnowledgeFilterSearchResult[]> => {
		const result = await fetchAgg<SearchListSpacesResponse, SearchListSpacesVariables>({
			graphQuery: SearchQuery,
			siteId: this.siteId,
			variables: {
				query,
				entityAti: this.entityTypeToAti(entityType),
				confluenceSpaces,
				projectTypes,
			},
		});

		return (
			result.data.search.search.edges
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				?.map((edge: any) => edge.node)
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				.map((item: any) => ({
					ari: item.ari,
					displayName: item.displayName,
					iconUrl: item.iconUrl,
					spaceKey: item.spaceKey,
					url: item.url,
				}))
		);
	};

	resolveObjects = async ({
		query,
		entityType,
	}: {
		query: string;
		entityType: EntityType;
	}): Promise<KnowledgeFilterSearchResult[] | null> => {
		const response = await fetch('/gateway/api/object-resolver/resolve/batch', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify([{ resourceUrl: query }]),
		});

		if (!response.ok || !response.body) {
			return null;
		}

		const data = (await response.json()) as ObjectResolveItemResponse[];

		if (!data || !Array.isArray(data)) {
			return null;
		}

		return data
			.filter(
				(item) =>
					item.body.meta.resourceType === entityType && item.body.meta.tenantId === this.siteId,
			)
			.map((item) => {
				const { data, meta } = item.body;

				// spaceKey is not available in the response, so we need to extract it from the URL
				// personal user space starts with "~"
				const spaceKey = data.url.match(/\/spaces\/([^/]+)/)?.[1].replace(/^~/, '') || '';
				const ari =
					data['atlassian:ari'] ||
					`ari:cloud:${meta.product}:${meta.tenantId}:${meta.resourceType}/${meta.objectId}`;

				return {
					ari,
					displayName: data.name,
					iconUrl: data.icon?.url,
					url: data.url,
					spaceKey,
				};
			});
	};

	getEntitiesByAris = async (options: {
		aris: string[];
	}): Promise<KnowledgeFilterSearchResult[]> => {
		let entities: Record<
			string,
			{
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				responseMapper: (response: any) => KnowledgeFilterSearchResult[];
				aris: string[];
				graphQuery: string;
			}
		> = {
			space: {
				responseMapper: (response: GqlSuccessResponse<ConfluenceSpacesResponse>) =>
					response?.data?.confluence?.spaces?.filter(notEmpty).map((space) => ({
						...space,
						url: space.links.base + space.links.webUi,
						iconUrl: space.iconUrl.path,
					})) ?? [],
				aris: [],
				graphQuery: GetSpacesQuery,
			},
			page: {
				responseMapper: (response: GqlSuccessResponse<ConfluencePagesResponse>) =>
					response?.data?.confluence?.pages?.filter(notEmpty).map((page) => ({
						...page,
						url: page.links.base + page.links.webUi,
					})) ?? [],
				aris: [],
				graphQuery: ConfluencePagesQuery,
			},
			project: {
				responseMapper: (response: GqlSuccessResponse<JiraProjectsResponse>) =>
					response?.data?.jira?.jiraProjects?.filter(notEmpty).map((project) => ({
						...project,
						iconUrl: project.iconUrl.small,
					})) ?? [],
				aris: [],
				graphQuery: JiraProjectsQuery,
			},
		};

		let helpCenterAri: string | undefined;

		for (const ari of options.aris) {
			try {
				const resourceType = ari.split(/[:\/]/)[4];

				if (resourceType === 'help-center') {
					helpCenterAri = ari;
					break; // Help center is always single
				} else if (entities[resourceType]) {
					entities[resourceType].aris.push(ari);
				}
			} catch {
				// Ignore invalid aris
			}
		}

		const aggPromises: Promise<KnowledgeFilterSearchResult[]>[] = [];

		for (const { aris, graphQuery, responseMapper } of Object.values(entities)) {
			if (aris.length) {
				aggPromises.push(
					fetchAgg<AggEntitiesResponse, AggEntitiesVariables>({
						graphQuery,
						variables: { aris },
					}).then((result) => {
						return responseMapper(result).map((item) => ({
							ari: item.ari,
							displayName: item.displayName,
							iconUrl: item.iconUrl,
							spaceKey: item.spaceKey,
							url: item.url,
						}));
					}),
				);
			}
		}

		if (helpCenterAri) {
			aggPromises.push(
				fetchAgg<HelpCenterByIdResponse, HelpCenterByIdVariables>({
					siteId: this.siteId,
					graphQuery: GetHelpCenterByIdQuery,
					variables: { cloudId: this.siteId, helpCenterAri },
				}).then((result) => {
					const data = (result as HelpCenterByIdResponse)?.data;
					const helpCenter = data?.helpCenter?.helpCenterById;
					if (helpCenter?.name?.default != null) {
						return [
							{
								ari: helpCenterAri,
								displayName: helpCenter.name.default,
								url: 'test',
							},
						];
					}
					return [];
				}),
			);
		}

		const results = await Promise.all(aggPromises).catch((error) => {
			logException({
				exception: error,
				name: 'KnowledgeFilterService.getEntitiesByAris',
			});

			return [];
		});

		return results.flat();
	};
}
